import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import HistoryPanel from '../components/HistoryPanel';
import { useNotificationSound } from '../hooks/useNotificationSound';
import DigitalClock from '../components/DigitalClock';
import NurseChecklistModal from '../components/NurseChecklistModal'; // [NEW]

export default function ServiceDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [checklistAlert, setChecklistAlert] = useState(null); // [NEW] Controla modal de checklist

  // Modo público/operacional
  const [viewMode, setViewMode] = useState(searchParams.get('mode') || 'operational');
  const [newAlertsCount, setNewAlertsCount] = useState(0);
  const lastAlertId = useRef(null);

  // Hook para sons personalizados
  const { playSound } = useNotificationSound();

  // Atualizar query params quando modo muda
  useEffect(() => {
    if (viewMode === 'public') {
      setSearchParams({ mode: 'public' });
      setShowHistory(false); // Ocultar histórico em modo público
    } else {
      setSearchParams({});
    }
  }, [viewMode, setSearchParams]);

  // Polling com frequência baseada no modo
  useEffect(() => {
    fetchAlerts();
    const interval = viewMode === 'public' ? 15000 : 10000; // 10s operacional, 15s público
    const pollInterval = setInterval(fetchAlerts, interval);
    return () => clearInterval(pollInterval);
  }, [filter, viewMode]);

  const fetchAlerts = async () => {
    try {
      // Se filter for 'all', buscar todos os tipos, senão filtrar por tipo específico
      const url = filter === 'all'
        ? '/service-alerts'
        : `/service-alerts?type=${filter}`;

      const response = await api.get(url);
      const newAlerts = response.data || [];

      // Detectar novos alertas no modo público
      if (viewMode === 'public' && newAlerts.length > 0) {
        if (lastAlertId.current !== null) {
          const newItems = newAlerts.filter(alert => alert.id > lastAlertId.current);
          if (newItems.length > 0) {
            setNewAlertsCount(prev => prev + newItems.length);
            // Tocar som do primeiro novo alerta
            if (newItems[0]?.service_type) {
              playSound(newItems[0].service_type);
            }
          }
        }

        const maxId = Math.max(...newAlerts.map(a => a.id));
        if (lastAlertId.current === null || maxId > lastAlertId.current) {
          lastAlertId.current = maxId;
        }
      }

      // Safely handle non-array responses to prevent crashes
      if (Array.isArray(newAlerts)) {
        setAlerts(newAlerts);
      } else {
        console.error('Invalid alerts data received:', newAlerts);
        setAlerts([]);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  // Agrupar alertas por leito (para mostrar juntos)
  const groupAlertsByBed = (alerts) => {
    const grouped = {};
    alerts.forEach(alert => {
      const bedKey = alert.bed_id || alert.bed_number;
      if (!grouped[bedKey]) {
        grouped[bedKey] = [];
      }
      grouped[bedKey].push(alert);
    });
    return grouped;
  };

  const toggleViewMode = () => {
    setViewMode(prev => prev === 'operational' ? 'public' : 'operational');
    setNewAlertsCount(0);
    lastAlertId.current = null;
  };

  // Atualizar status do leito
  const handleStatusChange = async (alertItem, newStatusId, actionDescription) => {
    setLoading(true);
    try {
      await api.patch(`/beds/${alertItem.bed_id}/status`, {
        statusId: newStatusId,
        notes: actionDescription
      });

      window.alert(`✅ ${actionDescription} registrado com sucesso!`);
      await fetchAlerts();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      window.alert('Erro ao atualizar status: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Concluir serviço
  const handleComplete = async (alertId, alertItem) => {
    if (alertItem.service_type === 'cleaning') {
      if (alertItem.bed_status_id !== 1) {
        window.alert('⚠️ Para remover este card, o leito deve estar com status "Disponível".\n\nUse os botões "Higienização Iniciada" e "Higienização Concluída" primeiro.');
        return;
      }

      if (!window.confirm('Confirma que deseja remover este card? O leito já está disponível.')) {
        return;
      }
    } else {
      if (!window.confirm(`Confirma que o serviço foi concluído?`)) {
        return;
      }
    }

    try {
      await api.patch(`/service-alerts/${alertId}`, { status: 'completed' });
      setAlerts(alerts.filter(a => a.id !== alertId));
    } catch (error) {
      console.error('Error completing alert:', error);
      window.alert('Erro ao concluir alerta: ' + error.message);
    }
  };

  // Renderizar botões (apenas no modo operacional)
  const renderActionButtons = (alert) => {
    if (viewMode === 'public') return null;

    // Serviço de Governanta (Housekeeper)
    if (alert.service_type === 'housekeeper') {
      // Se ainda não foi iniciado (status = pending)
      if (alert.status === 'pending') {
        return (
          <button
            className="btn btn-info w-100"
            onClick={() => api.patch(`/service-alerts/${alert.id}`, { status: 'in_progress' }).then(() => fetchAlerts())}
            disabled={loading}
          >
            <i className="bi bi-play-circle me-2"></i>
            🏨 Iniciar Acomodação
          </button>
        );
      }

      // Se foi iniciado (status = in_progress)
      if (alert.status === 'in_progress') {
        return (
          <button
            className="btn btn-success w-100"
            onClick={() => handleComplete(alert.id, alert)}
            disabled={loading}
          >
            <i className="bi bi-check-circle me-2"></i>
            ✅ Concluir Acomodação
          </button>
        );
      }
    }

    // Serviço de Lavanderia (Kit Admissão)
    if (alert.service_type === 'laundry') {
      // Se ainda não foi iniciado (status = pending)
      if (alert.status === 'pending') {
        return (
          <div className="d-flex flex-column gap-2">
            <button
              className="btn btn-info w-100"
              onClick={() => api.patch(`/service-alerts/${alert.id}`, { status: 'in_progress' }).then(() => fetchAlerts())}
              disabled={loading}
            >
              <i className="bi bi-play-circle me-2"></i>
              👔 Iniciando Entrega de Kit Admissão
            </button>
            <button
              className="btn btn-success w-100"
              onClick={() => handleComplete(alert.id, alert)}
              disabled={loading}
            >
              <i className="bi bi-check-all me-2"></i>
              ✅ Kit já entregue
            </button>
          </div>
        );
      }

      // Se foi iniciado (status = in_progress)
      if (alert.status === 'in_progress') {
        return (
          <button
            className="btn btn-success w-100"
            onClick={() => handleComplete(alert.id, alert)}
            disabled={loading}
          >
            <i className="bi bi-check-circle me-2"></i>
            ✅ Kit Admissão entregue
          </button>
        );
      }
    }

    // Serviço de Posto de Enfermagem
    if (alert.service_type === 'nursing') {
      const metadata = alert.metadata ? JSON.parse(alert.metadata) : {};

      if (metadata.type === 'bed_organization') {
        return (
          <button
            className="btn btn-success w-100"
            onClick={() => handleComplete(alert.id, alert)}
            disabled={loading}
          >
            <i className="bi bi-check-circle me-2"></i>
            ✅ Leito Arrumado
          </button>
        );
      }

      // [NEW] Preparação de Leito para Transferência (Passo 1)
      if (metadata.type === 'bed_preparation_transfer') {
        return (
          <button
            className="btn btn-info w-100 text-white fw-bold"
            onClick={() => handleComplete(alert.id, alert)}
            disabled={loading}
          >
            <i className="bi bi-box-arrow-in-down me-2"></i>
            Leito Pronto - Chamar Transporte
          </button>
        );
      }

      // [NEW] Checklist de Conferência da Reserva
      if (metadata.type === 'bed_conference') {
        return (
          <button
            className="btn btn-primary w-100"
            onClick={() => setChecklistAlert(alert)}
            disabled={loading}
          >
            <i className="bi bi-clipboard-check me-2"></i>
            Realizar Conferência
          </button>
        );
      }

      // [Legacy] Confirmação de Transferência Interna (Passo 3)
      if (metadata.type === 'transfer_confirmation') {
        return (
          <button
            className="btn btn-warning w-100 text-dark fw-bold"
            onClick={async () => {
              if (!window.confirm(`Confirmar a chegada do paciente ${metadata.patient_name} neste leito?`)) return;
              setLoading(true);
              try {
                // Executar Check-in (fulfills reservation & releases old bed)
                await api.post('/service-alerts/checkin', {
                  bedId: alert.bed_id,
                  patientName: metadata.patient_name,
                  gender: 'N/A', // Opcional ou pegar da metadata se tiver
                  admissionType: 'Transferência',
                  reservationId: metadata.reservation_id
                });

                // Concluir o alerta em seguida
                await api.patch(`/service-alerts/${alert.id}`, { status: 'completed' });

                alert('✅ Transferência Concluída com Sucesso!');
                fetchAlerts();
              } catch (err) {
                alert('Erro ao confirmar transferência: ' + err.message);
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
          >
            <i className="bi bi-person-check-fill me-2"></i>
            Confirmar Recebimento (Finalizar)
          </button>
        );
      }

      // Default: Desfazer leito
      return (
        <button
          className="btn btn-success w-100"
          onClick={() => handleComplete(alert.id, alert)}
          disabled={loading}
        >
          <i className="bi bi-check-circle me-2"></i>
          ✅ Leito Liberado para Higienização
        </button>
      );
    }

    // Serviço de Transporte / Maqueiro [NEW]
    if (alert.service_type === 'porter') {
      return (
        <button
          className="btn btn-success w-100"
          onClick={() => handleComplete(alert.id, alert)}
          disabled={loading}
        >
          <i className="bi bi-check-lg me-2"></i>
          Transporte Concluído
        </button>
      );
    }

    // Outros serviços (maintenance)
    if (alert.service_type !== 'cleaning') {
      return (
        <button
          className="btn btn-success w-100"
          onClick={() => handleComplete(alert.id, alert)}
          disabled={loading}
        >
          <i className="bi bi-check-circle me-2"></i>
          Marcar como Concluído
        </button>
      );
    }

    const bedStatus = alert.bed_status_id;

    if (bedStatus === 8) {
      return (
        <button
          className="btn btn-info w-100"
          onClick={() => handleStatusChange(alert, 4, 'Higienização iniciada')}
          disabled={loading}
        >
          <i className="bi bi-play-circle me-2"></i>
          🧹 Higienização Iniciada
        </button>
      );
    }

    if (bedStatus === 4) {
      return (
        <button
          className="btn btn-success w-100"
          onClick={() => handleStatusChange(alert, 1, 'Higienização concluída - Leito disponível')}
          disabled={loading}
        >
          <i className="bi bi-check-circle-fill me-2"></i>
          ✅ Higienização Concluída
        </button>
      );
    }

    if (bedStatus === 1) {
      return (
        <button
          className="btn btn-outline-secondary w-100"
          onClick={() => handleComplete(alert.id, alert)}
          disabled={loading}
        >
          <i className="bi bi-x-circle me-2"></i>
          Remover Card
        </button>
      );
    }

    return (
      <div className="alert alert-warning mb-0">
        Status do leito: {bedStatus || 'Desconhecido'}
      </div>
    );
  };

  const isPublicMode = viewMode === 'public';
  const containerStyle = {
    display: 'flex',
    height: isPublicMode ? '95vh' : '90vh',
    width: isPublicMode ? '95vw' : '90vw',
    margin: '0 auto',
    overflow: 'hidden'
  };

  return (
    <div style={containerStyle}>
      {/* Painel Principal */}
      {/* Relógio Digital Flutuante - Canto Superior Direito */}
      <div style={{ position: 'fixed', top: '20px', right: '30px', zIndex: 1000 }}>
        <DigitalClock />
      </div>

      <div style={{
        flex: showHistory && !isPublicMode ? '0 0 70%' : '1',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'flex 0.3s ease'
      }}>
        <div className="p-3">
          {/* Header com controles */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h1 className={`mb-0 ${isPublicMode ? 'fs-1' : ''}`}>
              Painel de Serviços
              {isPublicMode && <span className="badge bg-danger ms-3 pulse-animation">🔴 AO VIVO</span>}
            </h1>

            <div className="d-flex align-items-center">


              <div className="d-flex gap-2">
                {/* Toggle de Modo */}
                <button
                  className={`btn btn-${isPublicMode ? 'danger' : 'secondary'} btn-lg`}
                  onClick={toggleViewMode}
                  title={isPublicMode ? 'Modo Público (TV)' : 'Modo Operacional'}
                >
                  <i className={`bi bi-${isPublicMode ? 'tv' : 'gear'} me-2`}></i>
                  {isPublicMode ? '🖥️ Modo TV' : '⚙️ Operacional'}
                </button>

                {/* Toggle de Histórico (apenas modo operacional) */}
                {!isPublicMode && (
                  <button
                    className={`btn btn-${showHistory ? 'primary' : 'outline-primary'}`}
                    onClick={() => setShowHistory(!showHistory)}
                    title={showHistory ? 'Ocultar Histórico' : 'Mostrar Histórico'}
                  >
                    <i className="bi bi-clock-history me-2"></i>
                    {showHistory ? 'Ocultar' : 'Mostrar'} Histórico
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Contador de novos alertas (apenas modo público) */}
          {isPublicMode && newAlertsCount > 0 && (
            <div className="alert alert-info alert-dismissible fade show mb-3">
              <strong className="fs-5">{newAlertsCount} novo(s) alerta(s)</strong> desde sua última visualização
              <button
                type="button"
                className="btn-close"
                onClick={() => setNewAlertsCount(0)}
                aria-label="Fechar"
              />
            </div>
          )}

          {/* Filtro */}
          <div className="row mb-3">
            <div className={isPublicMode ? "col-md-8" : "col-md-6"}>
              <select
                className={`form-select ${isPublicMode ? 'form-select-lg' : ''}`}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">Todos os Serviços</option>
                <option value="cleaning">Higienização</option>
                <option value="laundry">Lavanderia</option>
                <option value="maintenance">Manutenção</option>
                <option value="housekeeper">Governanta (Admissões)</option>
                <option value="porter">Transporte (Maqueiro)</option>
                <option value="nursing">Enfermagem</option>
              </select>
            </div>
          </div>
        </div>

        {/* Área de Cards com Scroll */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 1rem 1rem 1rem' }}>
          {alerts.length === 0 && (
            <div className={`alert alert-info text-center ${isPublicMode ? 'fs-4 py-5' : ''}`}>
              <i className="bi bi-info-circle me-2"></i>
              {filter === 'all'
                ? 'Nenhum serviço pendente no momento'
                : `Nenhum serviço pendente para ${formatAlertType(filter)}`
              }
            </div>
          )}

          <div className="row">
            {alerts.map(alertItem => (
              <div
                key={alertItem.id}
                className={isPublicMode ? "col-md-6 col-lg-4 mb-4" : "col-md-6 col-lg-4 mb-3"}
                style={isPublicMode ? { animation: 'fadeIn 0.5s ease-in' } : {}}
              >
                <div
                  className={`card border-${getAlertColor(alertItem.service_type)} ${isPublicMode ? 'public-card' : ''}`}
                  style={isPublicMode ? {
                    borderWidth: '4px',
                    boxShadow: '0 6px 12px rgba(0,0,0,0.25)',
                    transform: 'scale(1.02)'
                  } : {
                    borderLeft: (alertItem.metadata && JSON.parse(alertItem.metadata).medical_discharge) ? '5px solid #6f42c1' : undefined
                  }}
                >
                  <div
                    className={`card-header bg-${getAlertColor(alertItem.service_type)} text-white ${isPublicMode ? 'fs-4 fw-bold py-3' : ''} d-flex justify-content-between align-items-center`}
                  >
                    <div>
                      <i className={`bi ${getAlertIcon(alertItem.service_type)} me-2`}></i>
                      {formatAlertType(alertItem.service_type)} - {alertItem.sector_name} - Leito {alertItem.bed_number}
                    </div>
                    {/* Relógio de Previsão de Alta */}
                    {alertItem.predictedDischargeDate && (
                      <div className="badge bg-white text-dark ms-2 d-flex align-items-center" title="Previsão de Alta">
                        <i className={`bi bi-clock${new Date(alertItem.predictedDischargeDate) < new Date() ? '-history text-danger' : ''} me-2`}></i>
                        <span className={new Date(alertItem.predictedDischargeDate) < new Date() ? 'text-danger fw-bold' : ''}>
                          {new Date(alertItem.predictedDischargeDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          {new Date(alertItem.predictedDischargeDate) < new Date() && <span className="ms-1">⚠️</span>}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className={`card-body ${isPublicMode ? 'p-4' : ''}`}>
                    {/* Changed p to div to avoid nesting warning */}
                    <div className={`card-text ${isPublicMode ? 'fs-5' : ''}`}>
                      <strong>Tipo:</strong> {(() => {
                        const meta = alertItem.metadata ? JSON.parse(alertItem.metadata) : {};

                        if (alertItem.service_type === 'nursing') {
                          if (meta.type === 'bed_preparation_transfer') return 'Arrumação de Leito (Transferência)';
                          if (meta.type === 'transfer_confirmation') return 'Recepção de Paciente';
                          return meta.type === 'bed_organization' ? 'Organização do Leito' : 'Desfazer Leito - Preparar para Higienização';
                        }

                        if (alertItem.service_type === 'porter') return 'Transporte de Paciente';

                        if (alertItem.service_type === 'cleaning' && meta.type === 'terminal_cleaning') {
                          return 'Limpeza Terminal';
                        }

                        return formatAlertType(alertItem.service_type);
                      })()}<br />
                      <strong>Prioridade:</strong> {getPriorityLabel(alertItem.priority)}<br />
                      <strong>Horário:</strong> {new Date(alertItem.created_at).toLocaleTimeString()}<br />
                      <strong>Data:</strong> {new Date(alertItem.created_at).toLocaleDateString()}

                      {/* Mostrar origem para Maqueiro */}
                      {alertItem.service_type === 'porter' && alertItem.metadata && JSON.parse(alertItem.metadata).origin_sector_name && (
                        <div className="mt-2 text-danger fw-bold">
                          <i className="bi bi-geo-alt-fill me-1"></i>
                          Origem: {JSON.parse(alertItem.metadata).origin_sector_name}
                          <br />
                          <i className="bi bi-arrow-right me-1"></i>
                          Destino: {alertItem.sector_name} - {alertItem.bed_number}
                        </div>
                      )}

                      {alertItem.service_type === 'cleaning' && (
                        <>
                          <br /><strong>Status Atual:</strong> {getBedStatusLabel(alertItem.bed_status_id)}
                        </>
                      )}

                      {/* Informações do Kit de Admissão (Lavanderia) */}
                      {alertItem.service_type === 'laundry' && alertItem.metadata && (() => {
                        try {
                          const metadata = JSON.parse(alertItem.metadata);
                          if (metadata.type === 'admission_kit') {
                            return (
                              <div className={`mt-2 p-2 bg-info border rounded ${isPublicMode ? 'fs-5' : ''}`}>
                                <i className="bi bi-box-seam me-1"></i>
                                <strong>Kit Admissão</strong>
                                {metadata.admission_type && (
                                  <div className="badge bg-white text-info mb-1 d-block">
                                    {metadata.admission_type}
                                  </div>
                                )}
                                <div className="small mt-1">
                                  {metadata.patient_gender && <span className="me-3">👤 {metadata.patient_gender}</span>}
                                  {metadata.patient_condition && <span>🚶 {metadata.patient_condition}</span>}
                                </div>
                                {/* Nome só aparece no modo operacional */}
                                {!isPublicMode && metadata.patient_name && (
                                  <div className="small text-muted mt-1">Paciente: {metadata.patient_name}</div>
                                )}
                              </div>
                            );
                          }
                        } catch (e) { }
                        return null;
                      })()}

                      {/* Precauções (Governanta) */}
                      {alertItem.service_type === 'housekeeper' && alertItem.precautions && alertItem.precautions !== 'Nenhuma' && (
                        <div className={`mt-2 p-2 bg-warning border rounded text-danger fw-bold ${isPublicMode ? 'fs-5' : ''}`}>
                          <i className="bi bi-shield-exclamation me-1"></i>
                          Precaução: {alertItem.precautions}
                          {/* Nome só aparece no modo operacional */}
                          {!isPublicMode && alertItem.patientName && <div className="small text-muted fw-normal mt-1">Paciente: {alertItem.patientName}</div>}
                        </div>
                      )}
                    </div>




                    <div className="d-flex gap-2">
                      {/* Botão de Cancelar - Pequeno e Discreto */}
                      {!isPublicMode && (
                        <button
                          className="btn btn-outline-danger d-flex align-items-center justify-content-center"
                          style={{ width: '40px', padding: '0' }}
                          onClick={async () => {
                            if (window.confirm('Tem certeza que deseja CANCELAR este serviço? Ele sumirá do painel.')) {
                              try {
                                await api.delete(`/service-alerts/${alertItem.id}`);
                                fetchAlerts();
                              } catch (err) {
                                window.alert('Erro ao cancelar: ' + err.message);
                              }
                            }
                          }}
                          title="Cancelar / Excluir Serviço"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                      <div className="flex-grow-1">
                        {renderActionButtons(alertItem)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Painel de Histórico (apenas modo operacional) */}
      {
        showHistory && !isPublicMode && (
          <div style={{
            flex: '0 0 40%',
            borderLeft: '2px solid #dee2e6',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <HistoryPanel />
          </div>
        )
      }

      {/* Estilos adicionais */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        .pulse-animation {
          animation: pulse 2s infinite;
        }
        
        .public-card {
          transition: all 0.3s ease;
        }
        
        .public-card:hover {
          transform: scale(1.05) !important;
        }
        
        .btn-outline-purple {
            color: #6f42c1;
            border-color: #6f42c1;
        }
        .btn-outline-purple:hover {
            background-color: #6f42c1;
            color: white;
        }
      `}</style>
      <NurseChecklistModal
        show={!!checklistAlert}
        onClose={() => setChecklistAlert(null)}
        alert={checklistAlert}
        onSuccess={() => { setChecklistAlert(null); fetchAlerts(); }}
      />
    </div>
  );
}

// Funções auxiliares
function getAlertColor(serviceType) {
  const colors = {
    housekeeper: 'primary',
    cleaning: 'success',
    laundry: 'info',
    maintenance: 'warning',
    nursing: 'danger',
    porter: 'dark' // [NEW] Maqueiro
  };
  return colors[serviceType] || 'secondary';
}

function getAlertIcon(serviceType) {
  const icons = {
    housekeeper: 'bi-person-check',
    cleaning: 'bi-droplet',
    laundry: 'bi-basket',
    maintenance: 'bi-tools',
    nursing: 'bi-heart-pulse',
    porter: 'bi-truck' // [NEW] Maqueiro
  };
  return icons[serviceType] || 'bi-bell';
}

function formatAlertType(serviceType) {
  const types = {
    housekeeper: 'Governanta',
    cleaning: 'Higienização',
    laundry: 'Lavanderia',
    maintenance: 'Manutenção',
    nursing: 'Posto de Enfermagem',
    porter: 'Transporte (Maqueiro)' // [NEW]
  };
  return types[serviceType] || serviceType;
}

function getPriorityLabel(priority) {
  const labels = {
    1: 'Normal',
    2: 'Média',
    3: 'Alta'
  };
  return labels[priority] || priority;
}

function getBedStatusLabel(statusId) {
  const labels = {
    1: '✅ Disponível',
    4: '🧹 Higienizando',
    8: '⏳ Aguardando Higienização'
  };
  return labels[statusId] || `Status ${statusId}`;
}
